import { CartItemInput } from '@storefront/data-commerce';
import { useOptionsStore } from '@storefront/hooks/useOptionsStore';
import { getLogger } from '@storefront/logging';
import { HeldError } from '@storefront/shared-components/error-information/held-error';
import { getProductCartData } from '@storefront/utils/prodRequests';
import sendOpenTelemetryEvent from '@storefront/utils/sendOpenTelemetryEvent';

import usePartner from './usePartner';
import { useAddProductsToCartMutation } from '../utils/checkout.mutation.utils';

const useMutateAddProductsCart = (
  cartItems?: CartItemInput | CartItemInput[]
) => {
  const { mutateAddProducts } = useAddProductsToCartMutation();
  const { hpChannel } = usePartner();
  const optionsStore = useOptionsStore();
  const cartInput = cartItems ?? getProductCartData(optionsStore.options);
  const logger = getLogger('pdp-config');

  const addProductsToCart = async (cartId: string, cartItems = cartInput) => {
    try {
      const { data } = await mutateAddProducts({
        variables: {
          cart_id: cartId,
          cartItems: cartItems,
          partnerInput: hpChannel
            ? {
                channel_code: hpChannel,
              }
            : null,
        },
      });

      const user_errors = data?.addProductsToCart?.user_errors;
      if (user_errors?.length > 0) {
        logger.error("Failed to add products to cart, found 'user errors'");
        sendOpenTelemetryEvent(
          'add-products-to-cart',
          JSON.stringify(user_errors)
        );
        user_errors.forEach(({ code, message }) =>
          logger.error({ code: code, msg: message })
        );
        const firstError = user_errors[0];
        const reason = {
          INSUFFICIENT_STOCK: 'OUT_OF_STOCK',
          UNDEFINED: 'MISSING_PRODUCT_SELECTION',
          PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
        };
        throw new HeldError(
          `errors.${
            reason[firstError.code as keyof typeof reason] ||
            'SOMETHING_WENT_WRONG'
          }`
        );
      }
    } catch (error: any) {
      sendOpenTelemetryEvent('add-products-to-cart', JSON.stringify(error));
      logger.error({
        msg: 'Error when trying to add products to cart',
        error: error.message,
      });
      throw error;
    }
  };
  return { addProductsToCart };
};

export default useMutateAddProductsCart;
